import React, { useRef, useEffect } from "react";
import { X, Plus, CircleDollarSign, CircleOff } from "lucide-react";

export const LOGO_SIZES = { sm: { h: 56, w: 200 }, md: { h: 84, w: 280 }, lg: { h: 120, w: 360 } };
import { fmtMoney, num, computeTotals, FONT_MAP } from "../lib/format";

const hexToRgba = (hex, a) => {
  if (!hex) return `rgba(15,23,42,${a})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const has = (v) => v !== undefined && v !== null && String(v).trim().length > 0;

const EText = ({ value, onChange, editable, className = "", style, placeholder, testid }) => (
  <input className={`edit-field ${className}`} style={style} value={value || ""} placeholder={editable ? placeholder : ""} disabled={!editable} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
);

// Item description: first line = title (bold), following lines = details (lighter, smaller). Rendered as rich text when not editing.
const ItemText = ({ value, onChange, editable, className = "", testid }) => {
  const text = value || "";
  const nl = text.indexOf("\n");
  if (!editable && nl > -1) {
    return (
      <div className={className} data-testid={testid}>
        <div className="font-semibold text-slate-900 leading-snug">{text.slice(0, nl)}</div>
        <div className="font-light text-slate-500 leading-snug whitespace-pre-line" style={{ fontSize: "0.9em" }}>{text.slice(nl + 1)}</div>
      </div>
    );
  }
  return <EArea value={value} onChange={onChange} editable={editable} className={`${className} ${nl > -1 ? "item-rich" : ""}`} placeholder="Descrição do item" testid={testid} />;
};

const EArea = ({ value, onChange, editable, className = "", style, placeholder, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, [value]);
  return <textarea ref={ref} rows={1} className={`edit-field ${className}`} style={style} value={value || ""} placeholder={editable ? placeholder : ""} disabled={!editable} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
};

const ENum = ({ value, onChange, editable, className = "", style, testid }) => (
  <input type="number" step="0.01" className={`edit-field ${className}`} style={style} value={value ?? 0} disabled={!editable} onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))} data-testid={testid} />
);

export default function A4Sheet({ layout, company = {}, theme = {}, data = {}, onChange, editable = false, branding = false }) {
  const primary = theme.primary || "#0f172a";
  const fontClass = FONT_MAP[theme.font] || "tpl-sans";
  const headerLabel = theme.headerLabel || "Orçamento";
  const logoRight = !!theme.logoRight;
  const totals = computeTotals(data);
  const items = data.items || [];
  const cur = data.currency || "BRL";
  const money = (n) => fmtMoney(n, cur);
  const show = (v) => editable || has(v);

  const set = (patch) => onChange && onChange({ ...data, ...patch });
  const setItem = (i, patch) => set({ items: items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const addItem = () => set({ items: [...items, { description: "", quantity: 1, unit_price: 0 }] });
  const addItemNoPrice = () => set({ items: [...items, { description: "", quantity: 1, unit_price: 0, no_price: true }] });
  const removeItem = (i) => set({ items: items.filter((_, idx) => idx !== i) });

  const logoBox = LOGO_SIZES[theme.logoSize] || LOGO_SIZES.md;
  const logo = (white = false) =>
    company.logo ? (
      <img src={company.logo} alt="logo" className="object-contain" data-testid="a4-logo" style={{ maxHeight: logoBox.h, maxWidth: logoBox.w, ...(white ? { filter: "brightness(0) invert(1)" } : {}) }} />
    ) : (
      <div className="font-semibold text-lg tracking-tight" style={{ color: white ? "#fff" : primary }}>{company.name || "Sua Empresa"}</div>
    );

  const companyInfo = (align = "right", white = false) => {
    const col = white ? "rgba(255,255,255,0.85)" : "#64748b";
    const strong = white ? "#fff" : "#1e293b";
    return (
      <div className="text-[11px] leading-relaxed" style={{ color: col, textAlign: align }}>
        {company.logo && has(company.name) && <div style={{ fontWeight: 600, color: strong, fontSize: 13 }}>{company.name}</div>}
        {has(company.doc) && <div>{company.doc}</div>}
        {has(company.address) && <div>{company.address}</div>}
        {has(company.phone) && <div>{company.phone}</div>}
        {has(company.email) && <div>{company.email}</div>}
        {has(company.web) && <div>{company.web}</div>}
      </div>
    );
  };

  const pair = (a, b) => {
    const ka = <React.Fragment key="a">{a}</React.Fragment>;
    const kb = <React.Fragment key="b">{b}</React.Fragment>;
    return logoRight ? [kb, ka] : [ka, kb];
  };

  const totalsBlock = (align = "right", boxed = false) => {
    const manual = totals.override > 0;
    if (!editable && totals.allUnpriced && !manual) return null;
    const breakdown = !manual && !totals.allUnpriced;
    const showDiscount = breakdown && (editable || totals.discount > 0);
    const showTax = breakdown && (editable || num(data.tax) > 0);
    const showOverride = editable && (totals.hasUnpriced || manual);
    return (
      <div className={`mt-6 flex ${align === "right" ? "justify-end" : "justify-start"}`}>
        <div className={`w-[300px] text-[13px] ${boxed ? "rounded-lg p-4" : ""}`} style={boxed ? { background: hexToRgba(primary, 0.06) } : {}}>
          {breakdown && <div className="flex justify-between py-1 text-slate-600"><span>Subtotal</span><span className="font-mono-jb">{money(totals.subtotal)}</span></div>}
          {showDiscount && (
            <div className="flex justify-between py-1 text-slate-600 items-center"><span>Desconto {editable && <span className="text-slate-400">(- {money(totals.discount)})</span>}</span><span className="font-mono-jb flex items-center">{editable ? <ENum value={data.discount} onChange={(v) => set({ discount: v })} editable className="w-20 text-right" /> : `- ${money(totals.discount)}`}</span></div>
          )}
          {showTax && (
            <div className="flex justify-between py-1 text-slate-600 items-center"><span>Impostos {editable && <span className="text-slate-400">(+ {money(totals.taxValue)})</span>}</span><span className="font-mono-jb flex items-center">{editable ? <span className="flex items-center"><ENum value={data.tax} onChange={(v) => set({ tax: v })} editable className="w-14 text-right" /><span className="ml-0.5">%</span></span> : `+ ${money(totals.taxValue)}`}</span></div>
          )}
          {showOverride && (
            <div className="pdf-exclude flex justify-between py-1 text-slate-600 items-center" data-testid="total-override-row">
              <span>Total da proposta <span className="text-slate-400 text-[11px]">(manual)</span></span>
              <ENum value={data.total_override} onChange={(v) => set({ total_override: v })} editable className="w-24 text-right" testid="total-override" />
            </div>
          )}
          {(breakdown || showOverride) && <div className="h-px my-2" style={{ background: hexToRgba(primary, 0.2) }} />}
          <div className="flex justify-between py-1 font-semibold text-[15px]" style={{ color: primary }}><span>Total</span><span className="font-mono-jb" data-testid="a4-total">{money(totals.total)}</span></div>
        </div>
      </div>
    );
  };

  const itemsTable = (variant = "default") => {
    const dense = variant === "dense";
    const solid = variant === "solid";
    const line = variant === "line";
    const priceCols = !totals.allUnpriced;
    const headStyle = solid
      ? { background: primary, color: "#fff" }
      : line
      ? { background: "transparent", color: "#64748b", borderBottom: `1px solid ${hexToRgba(primary, 0.35)}` }
      : { background: hexToRgba(primary, 0.06), color: "#64748b", borderBottom: `1.5px solid ${hexToRgba(primary, 0.5)}` };
    const vgrid = dense ? { borderLeft: "1px solid #eef2f6" } : {};
    const cell = `px-2 ${dense ? "py-1.5" : "py-2.5"}`;
    const toggleBtn = (i, it) => (
      <button onClick={() => setItem(i, { no_price: !it.no_price })} className={`transition-colors ${it.no_price ? "text-slate-400 hover:text-slate-900" : "text-slate-300 hover:text-slate-900"}`} data-testid={`toggle-price-${i}`} title={it.no_price ? "Mostrar valor deste item" : "Item sem valor (só descrição)"}>
        {it.no_price ? <CircleOff size={14} /> : <CircleDollarSign size={14} />}
      </button>
    );
    return (
      <div className="mt-6">
        <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
          <thead>
            <tr className={`text-[10px] uppercase tracking-wider ${dense ? "tpl-mono" : ""}`} style={headStyle}>
              <th className="text-left font-semibold py-2 px-2" style={{ width: priceCols ? (editable ? "44%" : "50%") : "auto" }}>Descrição</th>
              {priceCols && <th className="text-center font-semibold py-2 px-2 w-16">Qtd</th>}
              {priceCols && <th className="text-right font-semibold py-2 px-2">Valor Unit.</th>}
              {priceCols && <th className="text-right font-semibold py-2 px-2">Total</th>}
              {editable && <th className="w-12"></th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="align-top" style={{ borderBottom: "1px solid #eef2f6" }} data-testid={`item-row-${i}`} data-no-price={it.no_price ? "1" : "0"}>
                <td className={cell}><ItemText value={it.description} onChange={(v) => setItem(i, { description: v })} editable={editable} className="text-[12px] text-slate-700" testid={`item-desc-${i}`} /></td>
                {priceCols && it.no_price && <td className={cell} colSpan={3} style={vgrid} />}
                {priceCols && !it.no_price && (
                  <>
                    <td className={`${cell} text-center`} style={vgrid}>{editable ? <ENum value={it.quantity} onChange={(v) => setItem(i, { quantity: v })} editable className="text-center text-[12px] font-mono-jb" testid={`item-qty-${i}`} /> : <span className="text-[12px] font-mono-jb text-slate-700">{num(it.quantity)}</span>}</td>
                    <td className={`${cell} text-right`} style={vgrid}>{editable ? <ENum value={it.unit_price} onChange={(v) => setItem(i, { unit_price: v })} editable className="text-right text-[12px] font-mono-jb" testid={`item-price-${i}`} /> : <span className="text-[12px] font-mono-jb text-slate-700">{money(it.unit_price)}</span>}</td>
                    <td className={`${cell} text-right text-[12px] font-mono-jb text-slate-900`} style={vgrid}>{money(num(it.quantity) * num(it.unit_price))}</td>
                  </>
                )}
                {editable && (
                  <td className={`pdf-exclude ${cell}`}>
                    <div className="flex items-center gap-1.5 justify-end">
                      {toggleBtn(i, it)}
                      <button onClick={() => removeItem(i)} className="text-slate-300 hover:text-rose-500 transition-colors" data-testid={`remove-item-${i}`} title="Remover"><X size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <div className="pdf-exclude mt-3 flex items-center gap-4">
            <button onClick={addItem} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-900 transition-colors" data-testid="add-item-btn"><Plus size={14} /> Adicionar item</button>
            <button onClick={addItemNoPrice} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-400 hover:text-slate-900 transition-colors" data-testid="add-item-noprice-btn"><CircleOff size={14} /> Item sem valor</button>
          </div>
        )}
      </div>
    );
  };

  const clientBlock = (labelColor = "#94a3b8") => (
    <div>
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: labelColor }}>Para</div>
      <EArea value={data.client_name} onChange={(v) => set({ client_name: v })} editable={editable} className="text-[15px] font-semibold text-slate-900 leading-snug" placeholder="Nome do cliente" testid="a4-client-name" />
      {show(data.client_details) && <EArea value={data.client_details} onChange={(v) => set({ client_details: v })} editable={editable} className="text-[11px] text-slate-500 mt-0.5" placeholder="Detalhes do cliente" />}
    </div>
  );

  const dateField = (label, key, white = false) => (
    <div className="flex items-center gap-1" style={white ? { color: "rgba(255,255,255,0.8)" } : {}}>
      <span className={white ? "" : "text-slate-400"}>{label}:</span>
      {editable ? <EText value={data[key]} onChange={(v) => set({ [key]: v })} editable className="font-mono-jb" style={{ width: 92 }} placeholder="DD/MM/AAAA" testid={`date-${key}`} /> : <span className="font-mono-jb">{data[key]}</span>}
    </div>
  );

  const metaRow = (align = "flex-end") => {
    const si = show(data.issue_date);
    const sv = show(data.valid_until);
    if (!si && !sv) return null;
    return <div className="flex gap-6 text-[11px] text-slate-500 mt-1" style={{ justifyContent: align }}>{si && dateField("Emissão", "issue_date")}{sv && dateField("Válido até", "valid_until")}</div>;
  };

  const paymentBlock = (title = "Condições de pagamento") =>
    show(data.payment_terms) ? (<div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{title}</div><EArea value={data.payment_terms} onChange={(v) => set({ payment_terms: v })} editable={editable} className="text-[11px] text-slate-600" placeholder="Ex: 50% na aprovação, 50% na entrega" /></div>) : null;

  const notesBlock = () =>
    show(data.notes) ? (<div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Observações</div><EArea value={data.notes} onChange={(v) => set({ notes: v })} editable={editable} className="text-[11px] text-slate-600" placeholder="Observações adicionais" /></div>) : null;

  const terms = () => {
    const p = paymentBlock();
    const n = notesBlock();
    if (!p && !n) return null;
    return <div className="mt-8 grid grid-cols-2 gap-6">{p}{n}</div>;
  };

  const titleBlock = ({ serif = false, big = false } = {}) => (
    <div className="mt-8">
      {show(data.title) && <EArea value={data.title} onChange={(v) => set({ title: v })} editable={editable} className={`${serif ? "tpl-serif-head " : "font-bold "}${big ? "text-4xl" : serif ? "text-3xl" : "text-2xl"} text-slate-900 tracking-tight leading-tight`} placeholder="Título do orçamento" testid="a4-title" />}
      {show(data.intro) && <EArea value={data.intro} onChange={(v) => set({ intro: v })} editable={editable} className="text-[12px] text-slate-600 mt-2 leading-relaxed" placeholder="Breve introdução / apresentação" />}
      {has(data.image_url) && (
        <img src={data.image_url} alt="" className="mt-4 w-full rounded-xl object-cover" style={{ maxHeight: 220 }} crossOrigin="anonymous" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
    </div>
  );

  const renderInner = () => {
    switch (layout) {
      // 1) EDITORIAL MODERNO — faixa lateral colorida + título grande + tabela sólida
      case "modern_editorial":
        return (
          <div style={{ borderLeft: `6px solid ${primary}`, marginLeft: -56, paddingLeft: 50 }}>
            <div className="flex justify-between items-start">{pair(logo(), companyInfo("right"))}</div>
            {titleBlock({ big: true })}
            <div className="flex justify-between items-start mt-6">
              {clientBlock()}
              <div className="text-right"><div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: primary }}>{headerLabel}</div>{metaRow()}</div>
            </div>
            {itemsTable("solid")}
            {totalsBlock("right", true)}
            {terms()}
          </div>
        );
      // 2) CORPORATIVO — faixa escura no topo, ocupando largura total, texto branco
      case "corporate_bold":
        return (
          <>
            <div style={{ background: primary, margin: "-56px -56px 0 -56px", padding: "36px 56px", color: "#fff" }}>
              <div className="flex justify-between items-center">
                <div>{logo(true)}</div>
                <div className="text-right"><div className="text-2xl font-bold tracking-tight">{headerLabel.toUpperCase()}</div>{metaRow("flex-end") && <div className="opacity-80">{metaRow("flex-end")}</div>}</div>
              </div>
            </div>
            <div className="flex justify-between mt-8">{clientBlock()}{companyInfo("right")}</div>
            {titleBlock()}
            {itemsTable("default")}
            <div className="flex justify-between items-start">
              <div className="mt-6 max-w-[280px]">{paymentBlock("Condições")}</div>
              {totalsBlock("right", true)}
            </div>
            {notesBlock() && <div className="mt-4">{notesBlock()}</div>}
            <div className="mt-14"><div className="w-56 border-t border-slate-300 pt-1 text-[11px] text-slate-500">{company.name || "Assinatura"}</div></div>
          </>
        );
      // 3) TÉCNICO — monoespaçado, denso, com grade vertical
      case "compact_tech":
        return (
          <div className="tpl-mono">
            <div className="flex justify-between items-start">
              <div>{logo()}<div className="mt-2">{companyInfo("left")}</div></div>
              <div className="text-right"><div className="text-lg font-semibold" style={{ color: primary }}>{headerLabel.toUpperCase()}</div>{metaRow()}</div>
            </div>
            <div className="h-px my-5" style={{ background: primary }} />
            {clientBlock()}
            {titleBlock()}
            {itemsTable("dense")}
            {totalsBlock("right", false)}
            {terms()}
          </div>
        );
      // 4) ESTÚDIO CRIATIVO — serifada display, muito respiro, hairlines
      case "creative_studio":
        return (
          <>
            <div className="flex justify-between items-start">{logo()}<div className="text-right text-[11px] text-slate-400 tpl-mono space-y-0.5">{show(data.valid_until) && dateField("Válido até", "valid_until")}{show(data.issue_date) && dateField("Emissão", "issue_date")}</div></div>
            <div className="mt-16"><div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">{headerLabel}</div>{titleBlock({ serif: true, big: true })}</div>
            <div className="flex justify-between items-start mt-10">{clientBlock()}{companyInfo("right")}</div>
            {itemsTable("line")}
            {totalsBlock("right", false)}
            {terms()}
          </>
        );
      // 5) CLÁSSICO MINIMALISTA — wordmark centralizado, regras finas, editorial
      // 6) AURORA GLASS — cartão translúcido com brilho multicolor suave
      case "glass_aurora": {
        const aurora = "linear-gradient(120deg, rgba(66,133,244,0.16), rgba(52,168,83,0.13) 34%, rgba(251,188,5,0.13) 66%, rgba(234,67,53,0.16))";
        return (
          <>
            <div className="rounded-2xl p-6 ring-1 ring-white/70" style={{ background: aurora, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 30px -18px rgba(15,23,42,0.25)" }}>
              <div className="flex justify-between items-start">{pair(logo(), companyInfo("right"))}</div>
              <div className="mt-4"><span className="inline-block text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-white/75 text-slate-600 backdrop-blur">{headerLabel}</span></div>
            </div>
            <div className="flex justify-between items-start mt-6">{clientBlock()}<div className="text-right">{metaRow()}</div></div>
            {titleBlock()}
            {itemsTable("line")}
            {totalsBlock("right", true)}
            {terms()}
          </>
        );
      }
      // 7) GRADIENTE — faixa vibrante em gradiente, cantos suaves
      case "gradient_wave":
        return (
          <>
            <div style={{ background: `linear-gradient(115deg, ${primary}, ${hexToRgba(primary, 0.72)})`, margin: "-56px -56px 0", padding: "40px 56px", color: "#fff", borderBottomRightRadius: 44 }}>
              <div className="flex justify-between items-center">
                {logo(true)}
                <div className="text-2xl font-bold tracking-tight">{headerLabel}</div>
              </div>
            </div>
            <div className="flex justify-between items-start mt-8">{clientBlock()}<div className="text-right">{companyInfo("right")}{metaRow()}</div></div>
            {titleBlock()}
            {itemsTable("default")}
            {totalsBlock("right", true)}
            {terms()}
          </>
        );
      // 8) PASTEL — blocos suaves arredondados
      case "soft_pastel":
        return (
          <>
            <div className="flex justify-between items-start">{pair(logo(), companyInfo("right"))}</div>
            <div className="mt-6 rounded-3xl p-7" style={{ background: hexToRgba(primary, 0.07) }}>
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: primary }}>{headerLabel}</div>
              {titleBlock()}
              <div className="flex justify-between items-start mt-4">{clientBlock(primary)}<div className="text-right">{metaRow()}</div></div>
            </div>
            {itemsTable("line")}
            {totalsBlock("right", true)}
            {terms()}
          </>
        );
      // 9) GRADE — estrutura técnica em grade
      case "mono_grid":
        return (
          <div className="tpl-mono">
            <div className="grid grid-cols-2 ring-1 ring-slate-200 rounded-lg overflow-hidden">
              <div className="p-4 border-r border-slate-200">{logo()}<div className="mt-2">{companyInfo("left")}</div></div>
              <div className="p-4">{clientBlock()}{metaRow("flex-start")}</div>
            </div>
            <div className="mt-6"><div className="text-[10px] tracking-[0.2em]" style={{ color: primary }}>{headerLabel.toUpperCase()}</div>{titleBlock()}</div>
            {itemsTable("dense")}
            {totalsBlock("right", false)}
            {terms()}
          </div>
        );
      // 10) PRESTÍGIO — barra lateral escura premium
      case "prestige_dark":
        return (
          <div className="flex" style={{ margin: -56, minHeight: 1123 }}>
            <div style={{ width: 216, background: "#0f172a", color: "#fff", padding: "44px 26px" }} className="flex flex-col justify-between shrink-0">
              <div>
                {logo(true)}
                <div className="mt-8">{companyInfo("left", true)}</div>
              </div>
              <div className="h-1 w-12 rounded-full" style={{ background: primary }} />
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "rgba(255,255,255,0.6)" }}>{headerLabel}</div>
            </div>
            <div style={{ flex: 1, padding: "44px 40px" }}>
              <div className="flex justify-between items-start">{clientBlock()}<div className="text-right">{metaRow()}</div></div>
              {titleBlock()}
              {itemsTable("default")}
              {totalsBlock("right", true)}
              {terms()}
            </div>
          </div>
        );
      case "minimal_classic":
      default:
        return (
          <>
            <div className="text-center pb-5" style={{ borderBottom: "1px solid #e2e8f0" }}>
              <div className="inline-block">{logo()}</div>
              <div className="mt-2 text-[11px] text-slate-500">{[company.doc, company.phone, company.email, company.web].filter(has).join("  ·  ")}</div>
            </div>
            <div className="flex justify-between items-start mt-8">
              {clientBlock()}
              <div className="text-right"><div className="text-xl font-semibold tracking-tight text-slate-900">{headerLabel}</div>{metaRow()}</div>
            </div>
            {titleBlock()}
            {itemsTable("line")}
            {totalsBlock("right", false)}
            {terms()}
          </>
        );
    }
  };

  const noPadTop = layout === "corporate_bold" || layout === "modern_editorial";
  return (
    <div className={`a4-sheet keep-light ${fontClass} ${editable ? "editable" : ""} text-slate-900`} style={{ position: "relative", padding: "56px", paddingTop: noPadTop ? 56 : 56, paddingBottom: branding ? 68 : 56 }} data-testid="a4-sheet">
      {renderInner()}
      {branding && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 18, textAlign: "center", fontSize: 9, letterSpacing: "0.08em", color: "rgba(148,163,184,0.9)", pointerEvents: "none" }} data-testid="basiq-brand">
          Feito com <span style={{ fontWeight: 700 }}>Basiq</span>
        </div>
      )}
    </div>
  );
}
